import { BaseMapper } from '@ps/utils';
import {
  INITIAL_LOCATION_UPLOAD_RESULT,
  SurveyLocationUploadResult,
  SurveyQualificationCondition,
  withoutEmptyGroups,
} from '@ps/survey-utils';

import {
  FsaUploadResponseDto,
  LocationCodeDto,
} from '../dtos/location-upload.dto';

// An FSA's code is its backend id; the label (A1A) is its name.
const _condition = ({
  id,
  name,
}: LocationCodeDto): SurveyQualificationCondition => ({
  id,
  name: name || id,
});

export class FsaUploadMapper extends BaseMapper<
  FsaUploadResponseDto,
  SurveyLocationUploadResult
> {
  public fromDto(dto: FsaUploadResponseDto): SurveyLocationUploadResult {
    const summary = dto.zipcodes;
    const validCodes = summary.fsaList.map(_condition);

    // The FSA response reports how many codes were rejected but not which, and has no modified
    // count, so incorrectCodes and modifiedCodeCount keep their defaults.
    return {
      ...INITIAL_LOCATION_UPLOAD_RESULT,
      validCodes,
      totalCodeCount: summary.total,
      correctCodeCount: summary.correct,
      duplicateCodeCount: summary.duplicate,
      incorrectCodeCount: summary.incorrect,
      filePath: dto.fsaFilePath,
      groupedCodes: this._groupedIds(summary.grouped, validCodes),
    };
  }

  // The upload groups FSAs by label, but codes are ids everywhere else (selection, quotas, the
  // location-groups endpoint). A label the list does not know is dropped, and so is a group left
  // with nothing.
  private _groupedIds(
    groupedLabels: Record<string, string[]>,
    validCodes: readonly SurveyQualificationCondition[],
  ): Record<string, string[]> {
    const idByLabel = new Map(validCodes.map(({ id, name }) => [name, id]));

    return withoutEmptyGroups(
      Object.fromEntries(
        Object.entries(groupedLabels).map(([groupName, labels]) => [
          groupName,
          labels.flatMap((label) => idByLabel.get(label) ?? []),
        ]),
      ),
    );
  }
}
